import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV } from "@/lib/content";
import { scrollToId } from "@/lib/site";

const useActiveSection = () => {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const els = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return active;
};

const MobileMenu = ({ go }) => (
  <motion.div
    id="mobile-menu"
    data-testid="mobile-menu"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25 }}
    className="fixed inset-x-0 bottom-0 top-[72px] z-40 overflow-y-auto bg-ink lg:hidden"
  >
    <ul className="wrap flex flex-col pb-10 pt-4">
      {NAV.map((n, i) => (
        <motion.li
          key={n.id}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <a
            href={`#${n.id}`}
            onClick={go(n.id)}
            data-testid={`mobile-nav-link-${n.id}`}
            className="flex min-h-[64px] items-center justify-between border-b border-white/10 font-display text-[32px] font-bold uppercase text-white active:text-emerald-dot"
          >
            {n.label}
            <span className="font-mono text-xs text-white/40">0{i + 1}</span>
          </a>
        </motion.li>
      ))}
      <motion.li initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.45 }} className="pt-8">
        <a href="#contact" onClick={go("contact")} data-testid="mobile-nav-cta" className="btn-primary w-full !py-4">
          START A PROJECT <ArrowUpRight className="h-4 w-4" />
        </a>
        <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">www.faizu.net</p>
      </motion.li>
    </ul>
  </motion.div>
);

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const active = useActiveSection();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    document.body.style.overflow = "";
    requestAnimationFrame(() => scrollToId(id));
  };

  return (
    <>
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open ? "bg-ink/95 shadow-[0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-[10px]" : "bg-transparent"
      }`}
    >
      <nav className="wrap flex h-[72px] items-center justify-between" aria-label="Main">
        <a href="#home" onClick={go("home")} data-testid="nav-logo" aria-label="FAIZ home">
          <Logo onDark />
        </a>
        <ul className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <li key={n.id}>
              <a
                href={`#${n.id}`}
                onClick={go(n.id)}
                data-testid={`nav-link-${n.id}`}
                aria-current={active === n.id ? "true" : undefined}
                className={`relative rounded-full px-4 py-2 text-[14px] font-medium transition-colors duration-200 ${
                  active === n.id ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {n.label}
                <span
                  className={`absolute bottom-0.5 left-1/2 h-[3px] w-[3px] -translate-x-1/2 rounded-full bg-emerald-dot transition-opacity duration-200 ${
                    active === n.id ? "opacity-100" : "opacity-0"
                  }`}
                />
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          <a href="#contact" onClick={go("contact")} data-testid="nav-cta-start-project" className="btn-primary hidden !px-5 !py-3 sm:inline-flex">
            START A PROJECT <ArrowUpRight className="btn-arrow h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            className="grid h-12 w-12 place-items-center rounded-full border border-white/15 text-white transition-colors duration-200 hover:bg-white/10 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
    </header>
    <AnimatePresence>{open && <MobileMenu go={go} />}</AnimatePresence>
    </>
  );
}
